/**
 * Wie der **Marker** eines **Texts** aussieht (CONTEXT.md, Text → Hervorheben;
 * `.claude/tasks/canvas-text-marker-look.md`): wie mit einem echten Textmarker
 * gezogen — flach in einer Farbe, ein Hauch schief, mit wechselnden Enden, die
 * über den Text hinausragen.
 *
 * Hier steht nur die Rechnung: welche Stücke einen **Markerstrich** bilden, wo seine
 * Enden sitzen und welche Form er bekommt. Gezeichnet wird in `TextNode.scss`
 * (Maske auf einer eigenen Ebene), gesetzt von `CanvasTextContent` (in Ruhe)
 * und vom Dekorations-Plugin (im Schreibfeld) — beide mit denselben Klassen.
 */

import { canvasTextMarkerShape, type CanvasTextMarkerShapeSpec } from './canvasTextMarkerShape'

/** Wie viele Formen es je Ende gibt (`.text-node__marker--s0` … `--e3`). */
export const CANVAS_TEXT_MARKER_VARIANTS = 4

/**
 * Die Formnummern eines Markers (`<mark data-look>`, ADR 0029) — eine Zahl,
 * aus der die Form folgt. „Marker wechseln" vergibt neue (gewünscht 2026-09-23).
 */
export const CANVAS_TEXT_MARKER_LOOKS = 10000

/** Eine neue Formnummer, anders als `except`. */
export function randomCanvasTextMarkerLook(except?: number | null, random: () => number = Math.random): number {
  for (;;) {
    const look = Math.floor(random() * CANVAS_TEXT_MARKER_LOOKS)
    if (look !== except) return look
  }
}

/** Die Richtung, in die ein Markerstrich ansteigt — „ein Hauch schief". */
export type CanvasTextMarkerTilt = 'up' | 'down' | 'flat'

/** Ein Stück eines Markerstrichs: ein Textlauf mit Marker. */
export interface CanvasTextMarkerPiece {
  /** Die Nummer des Markerstrichs im ganzen Text, ab 0. */
  stroke: number
  /** Ob hier der Markerstrich beginnt bzw. endet — nur dort sitzen Kappe und Überstand. */
  start: boolean
  end: boolean
  /** Aus wie vielen Stücken der Markerstrich besteht. */
  pieces: number
  /** Die gespeicherte Formnummer, sofern der Marker eine trägt. */
  look?: number
}

/**
 * Die Markerstriche einer ZEILE: aufeinanderfolgende Läufe mit derselben Marker-Farbe
 * bilden einen Markerstrich; ein Lauf ohne Marker oder mit einer anderen Farbe beendet
 * ihn. Jede Zeile hat ihre eigenen Markerstriche (entschieden 2026-09-23).
 *
 * @param highlights je Lauf der Zeile seine Marker-Farbe, `null` ohne Marker
 * @param firstStroke die Nummer, mit der der erste Markerstrich dieser Zeile zählt
 * @returns je Lauf sein Stück (oder `null`) und die Nummer des nächsten Markerstrichs
 *
 * @example
 * canvasTextMarkerRuns(['pink', 'pink', null, 'green'], 0)
 * // pieces: [{ stroke: 0, start: true, end: false, pieces: 2 },
 * //          { stroke: 0, start: false, end: true, pieces: 2 },
 * //          null,
 * //          { stroke: 1, start: true, end: true, pieces: 1 }], next: 2
 */
export function canvasTextMarkerRuns(
  highlights: readonly (string | null | undefined)[],
  firstStroke = 0,
  looks: readonly (number | null | undefined)[] = [],
): { pieces: (CanvasTextMarkerPiece | null)[]; next: number } {
  const pieces: (CanvasTextMarkerPiece | null)[] = highlights.map(() => null)
  // Ein Markerstrich = dieselbe Farbe UND dieselbe Formnummer.
  const same = (a: number, b: number) => highlights[a] === highlights[b] && (looks[a] ?? null) === (looks[b] ?? null)
  let stroke = firstStroke
  let index = 0
  while (index < highlights.length) {
    const color = highlights[index]
    if (!color) {
      index += 1
      continue
    }
    let last = index
    while (last + 1 < highlights.length && same(last + 1, index)) last += 1
    const count = last - index + 1
    const look = looks[index] ?? undefined
    for (let i = index; i <= last; i += 1) {
      pieces[i] = { stroke, start: i === index, end: i === last, pieces: count, ...(look === undefined ? {} : { look }) }
    }
    stroke += 1
    index = last + 1
  }
  return { pieces, next: stroke }
}

/** Die Form eines Markerstrichs. */
export interface CanvasTextMarkerLook {
  /** Die Saat einer berechneten Form (`canvasTextMarkerShape`). */
  seed: number
  startVariant: number
  endVariant: number
  tilt: CanvasTextMarkerTilt
  /** Versatz nach unten (positiv) oder oben, in em. */
  offset: number
}

/** FNV-1a — klein, schnell, gleichmäßig genug für eine Formwahl. */
function hash(text: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/** Meistens schief, manchmal gerade — ein echter Markerstrich ist selten ganz waagerecht. */
const TILTS: readonly CanvasTextMarkerTilt[] = ['up', 'down', 'up', 'down', 'flat']

/**
 * Die Form eines Markerstrichs — FEST, nicht zufällig: aus seiner gespeicherten
 * Formnummer (`data-look`), sonst aus dem Text und der Nummer des
 * Markerstrichs. Neu zeichnen, verschieben oder darin weitertippen ändert sie
 * nicht; nur „Marker wechseln" (entschieden 2026-09-23).
 *
 * Ein Markerstrich aus mehreren Stücken (ein fettes Wort darin) liegt gerade: Jedes
 * Stück ist ein eigenes Element, und eine Neigung finge in jedem neu an.
 */
export function canvasTextMarkerLook(seed: string, piece: Pick<CanvasTextMarkerPiece, 'stroke' | 'pieces' | 'look'>): CanvasTextMarkerLook {
  // Trägt der Marker eine Formnummer, gilt sie — unabhängig davon, wo er steht.
  const h = hash(piece.look === undefined ? `${seed}:${piece.stroke}` : `look:${piece.look}`)
  return {
    seed: h,
    startVariant: h % CANVAS_TEXT_MARKER_VARIANTS,
    endVariant: (h >>> 3) % CANVAS_TEXT_MARKER_VARIANTS,
    tilt: piece.pieces > 1 ? 'flat' : TILTS[(h >>> 7) % TILTS.length]!,
    offset: (((h >>> 11) % 5) - 2) * 0.015,
  }
}

/** Die Klassen eines Stücks — dieselben in Ruhe und im Schreibfeld. */
export function canvasTextMarkerClass(piece: CanvasTextMarkerPiece, look: CanvasTextMarkerLook): string {
  return [
    'text-node__marker',
    `text-node__marker--${look.tilt}`,
    piece.start && `text-node__marker--start text-node__marker--s${look.startVariant}`,
    piece.end && `text-node__marker--end text-node__marker--e${look.endVariant}`,
  ].filter(Boolean).join(' ')
}

/**
 * Die Variablen eines Stücks: Versatz, und mit berechneter Form (`spec`,
 * der Projekttitel) auch Form und Überstand — dieselben in Ruhe und im
 * Schreibfeld. `text` ist der Text des Stücks; aus seiner Länge folgt, wie
 * Wellen und Streifen in die Form gelegt werden.
 */
export function canvasTextMarkerVars(
  piece: CanvasTextMarkerPiece,
  look: CanvasTextMarkerLook,
  text: string,
  spec?: CanvasTextMarkerShapeSpec,
): Record<string, string> {
  const vars: Record<string, string> = { '--marker-offset': `${look.offset}em` }
  if (!spec) return vars
  const shape = canvasTextMarkerShape(spec, { seed: look.seed, text, start: piece.start, end: piece.end, straight: piece.pieces > 1 })
  vars['--marker-shape'] = shape.mask
  vars['--marker-start-overhang'] = `${shape.startOverhang}em`
  vars['--marker-end-overhang'] = `${shape.endOverhang}em`
  vars['--marker-opacity'] = String(spec.opacity)
  return vars
}

/**
 * Wozu ein Stück gehört — für eine Ebene, die die Markerstriche selbst
 * zeichnet und dafür die Stücke je Zeile zusammensucht (`TitleMarkerLayer`).
 */
export function canvasTextMarkerData(piece: CanvasTextMarkerPiece, look: CanvasTextMarkerLook): Record<string, string> {
  return { 'data-marker-stroke': String(piece.stroke), 'data-marker-seed': String(look.seed) }
}

/** Die Klassen eines Stücks mit berechneter Form — nur Art und Enden, keine Kappen. */
export function canvasTextShapedMarkerClass(piece: CanvasTextMarkerPiece): string {
  return [
    'text-node__marker text-node__marker--shaped',
    piece.start && 'text-node__marker--start',
    piece.end && 'text-node__marker--end',
  ].filter(Boolean).join(' ')
}

/** Ob zwei Formen gleich AUSSEHEN — der kleine Versatz zählt nicht, man sieht ihn kaum. */
function sameShape(a: CanvasTextMarkerLook, b: CanvasTextMarkerLook): boolean {
  return a.startVariant === b.startVariant && a.endVariant === b.endVariant && a.tilt === b.tilt
}

/**
 * Die Formnummer für „Marker wechseln": nie eine, die genauso aussieht wie die
 * bisherige (anderer Anfang, anderes Ende oder andere Lage) — sonst täte ein
 * Klick bei jedem 48. Markerstrich scheinbar nichts (gewünscht 2026-09-23).
 * Ohne bisherige Nummer (eingefügt, älter) genügt irgendeine.
 */
export function shuffledCanvasTextMarkerLook(previous: number | null, pieces: number, random: () => number = Math.random): number {
  const before = previous === null ? null : canvasTextMarkerLook('', { stroke: 0, pieces, look: previous })
  for (;;) {
    const look = randomCanvasTextMarkerLook(previous, random)
    if (!before || !sameShape(before, canvasTextMarkerLook('', { stroke: 0, pieces, look }))) return look
  }
}
